import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import "./App.css";

// Components
import Sidebar from "./components/Sidebar";
import Login from "./auth/Login";
import Dashboard from "./components/Dashboard";
import ForgotPassword from "./auth/ForgotPassword";
import ResetPassword from "./auth/ResetPassword";

// Auth utilities
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { getValidToken, clearAllStorage, getUserData } from "./auth/authUtils";
import { setupTokenRefresh, validateTokens } from "./utils/tokenRefresh";
import { apiClient } from "./config/api";

// Debug utilities
import { debugLog, debugInfo, isDebugMode } from "./utils/debug";

// Hooks
import { useUserSettings } from "./hooks/useUserSettings";

const App = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);

  // Логирование изменений user для отладки
  useEffect(() => {
    console.log("🔍 [App] User state changed:", user);
  }, [user]);

  useEffect(() => {
    const checkAuthStatus = async () => {
      // Вспомогательная функция: пробует обновить access token через refresh token
      // При успехе — устанавливает пользователя и планирует дальнейший рефреш
      // При неудаче — очищает всё и показывает логин
      const tryRefreshAndSetUser = async () => {
        const refreshToken = apiClient.getRefreshToken();
        if (!refreshToken) {
          debugLog("App", "No refresh token available - redirecting to login");
          clearAllStorage();
          setUser(null);
          return;
        }
        try {
          console.log("🔄 [App] Access token expired on load, attempting refresh...");
          const newTokens = await apiClient.refreshAccessToken();
          if (newTokens && newTokens.access_token) {
            const userData = getUserData();
            if (userData && userData.email) {
              setUser(userData);
              debugLog("App", "Session restored after token refresh:", userData.email);
            } else {
              setUser({ authenticated: true });
            }
            const expiresIn = newTokens.expires_in || 3600;
            setupTokenRefresh(expiresIn);
            console.log(`✅ [App] Session restored, next refresh in ${expiresIn}s`);
          } else {
            clearAllStorage();
            setUser(null);
          }
        } catch (refreshError) {
          console.log("🔒 [App] Token refresh failed on load:", refreshError.message);
          clearAllStorage();
          setUser(null);
        }
      };

      try {
        debugInfo("App", "Checking authentication status...");
        
        // Сначала проверяем валидность токенов
        const tokensValid = validateTokens();
        if (!tokensValid) {
          console.log("🔒 Tokens invalid or missing - clearing auth");
          clearAllStorage();
          setUser(null);
          setIsLoading(false);
          return;
        }
        
        const token = await getValidToken();

        if (token) {
          // User is authenticated - загружаем данные пользователя
          const userData = getUserData();
          
          console.log("🔍 [App] Token found, userData from localStorage:", userData);
          
          if (userData && userData.email) {
            setUser(userData);
            debugLog("App", "User authenticated with valid token:", userData.email);
          } else {
            // Если нет данных пользователя, но есть токен - устанавливаем минимальные данные
            setUser({ authenticated: true });
            debugLog("App", "User authenticated but no user data found");
          }
          
          // Восстанавливаем автоматическое обновление токена
          const tokenExpiry = localStorage.getItem("tokenExpiry");
          if (tokenExpiry) {
            const now = new Date().getTime();
            const expiresIn = Math.max(0, Math.floor((parseInt(tokenExpiry) - now) / 1000));
            if (expiresIn > 0) {
              setupTokenRefresh(expiresIn);
              debugLog("App", `Token refresh scheduled for ${expiresIn}s`);
            } else {
              // tokenExpiry уже устарел — пробуем обновить токен перед сдачей
              await tryRefreshAndSetUser();
            }
          }
        } else {
          // Access token истёк или отсутствует — пробуем обновить через refresh token
          await tryRefreshAndSetUser();
        }
      } catch (error) {
        debugLog("App", "Auth check failed:", error);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const handleLogin = (userData) => {
    console.log("🔐 [App] handleLogin called with userData:", userData);
    
    // Проверяем, нужно ли обновлять состояние
    if (!user || user.email !== userData.email) {
      setUser(userData);
      debugLog("App", "User logged in successfully");
    } else {
      console.log("⚠️ [App] User already set, skipping setState");
    }
  };

  const handleLogout = () => {
    clearAllStorage();
    setUser(null);
    debugLog("App", "User logged out");
  };

  if (isLoading) {
    return (
      <div className="App">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Загрузка...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthProvider value={{ user, login: handleLogin, logout: handleLogout }}>
      <div className="App">
        <Router>
          {user ? (
            <Routes>
              <Route path="/" element={<Dashboard onLogout={handleLogout} />} />
              <Route path="/dashboard" element={
                <div className="app-container">
                  <Sidebar onLogout={handleLogout} />
                  <main className="main-content">
                    <Dashboard />
                  </main>
                </div>
              } />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          ) : (
            <Routes>
              <Route path="/login" element={<Login onLogin={handleLogin} />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          )}
        </Router>
      </div>
    </AuthProvider>
  );
};

export default App;
